import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import dynamic from 'next/dynamic';
import useTranslation from 'next-translate/useTranslation';

// Utils
import clsx from 'clsx';

// Context
import { usePost } from '@/hooks/context/usePost';
// Hooks
import useAcl from '@/utils/hooks/user/useAcl';

import useWindowSize from '@/utils/hooks/utils/useWindowSize';

import PropTypes from 'prop-types';

import { aclFeatures } from '@/lib/acl/aclFeatures';
// Lib
import { POST } from '@/lib/posts/postTypes';

// Components
import Icon from '@/components/base/Icon';
import Post from '@/components/base/Post';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';

const CustomPopover = dynamic(() => import('@/components/composite/Popover/CustomPopover'));
const AlbumsPopover = dynamic(() => import('@/components/composite/Post/AlbumsPopover'));
const LabelsPopover = dynamic(() => import('@/components/composite/Post/LabelsPopover'));

const ActionButton = ({ icon, onClick, ...restProps }) => {
    const { t } = useTranslation();
    const { hasAcl } = useAcl();

    const isLocked = !hasAcl(aclFeatures.content.campaign.read);

    const renderIcon = () => {
        if (isLocked) {
            return (
                <TooltipPlanRestriction position="top">
                    <Icon icon="crown" className="text-icoBlue" />
                </TooltipPlanRestriction>
            );
        }
        return <Icon icon="plus-sign" className="text-sm text-white" />;
    };

    return (
        <button
            className={clsx(
                'flex items-center rounded-2xl bg-white/20 px-xxs py-2 text-white !ring-offset-0',
                { 'space-x-2': isLocked, 'space-x-1': !isLocked }
            )}
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            title={t('commons:media.albums.add_to_album')}
            disabled={isLocked}
            {...restProps}>
            <Icon icon={icon} className="text-base text-white" />
            {renderIcon()}
        </button>
    );
};

ActionButton.propTypes = {
    icon: PropTypes.string,
    onClick: PropTypes.func
};

const HoverLabelAndAlbums = ({
    openViewer,
    showAlbums,
    showLabels,
    referenceElement,
    handleAlbumsEvent,
    handleLabelsEvent,
    postType = POST,
    socialProfile
}) => {
    const { t } = useTranslation();
    const { data } = usePost();
    const albums = data?.albums || [];
    const labels = data?.labels || [];

    const { width: windowWidth } = useWindowSize();
    const [isOpenLabels, setIsOpenLabels] = useState(false);
    const [isOpenAlbums, setIsOpenAlbums] = useState(false);
    const [options, setOptions] = useState({
        placement: 'right-end',
        offset: [-8, 10]
    });
    const [popperElement, setPopperElement] = useState();
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: options.placement,
        modifiers: [
            {
                name: 'offset',
                options: { offset: options.offset }
            }
        ]
    });

    useEffect(() => {
        const newOptions =
            windowWidth > 675
                ? { placement: 'right-end', offset: [-8, 10] }
                : { placement: 'bottom-start', offset: [0, 25] };
        if (
            newOptions.placement !== options.placement ||
            newOptions.offset[0] !== options.offset[0]
        ) {
            setOptions(newOptions);
        }
         
    }, [windowWidth]);

    const renderAlbums = () => {
        if (!showAlbums) return null;
        return (
            <div className="flex flex-wrap items-center gap-2">
                {albums.length > 0 && (
                    <>
                        <span className="inline-flex h-8 max-w-[56px] items-center rounded-2xl bg-white/20 px-xxs py-2 text-xs capitalize text-white">
                            <span className="truncate">{albums[0]?.name}</span>
                        </span>
                        {albums.length > 1 && (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white">
                                <span className="flex items-center">
                                    <span className="text-xs">+{albums.length - 1}</span>
                                </span>
                            </div>
                        )}
                    </>
                )}
                <ActionButton
                    icon="albums"
                    onClick={() => setIsOpenAlbums(!isOpenAlbums)}
                    aria-label="add-album"
                    title={t('commons:media.albums.add_to_album')}
                />
            </div>
        );
    };

    const renderLabels = () => {
        if (!showLabels) return null;
        return (
            <div className="flex flex-wrap items-center gap-2">
                {labels.length > 0 && (
                    <>
                        <span className="inline-flex h-8 max-w-[56px] items-center rounded-2xl bg-white/20 px-xxs py-2 text-xs capitalize text-white">
                            <span className="truncate">{labels[0]?.label}</span>
                        </span>
                        {labels.length > 1 && (
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white">
                                <span className="flex items-center">
                                    <span className="text-xs">+{labels.length - 1}</span>
                                </span>
                            </div>
                        )}
                    </>
                )}
                <ActionButton
                    icon="labels"
                    onClick={() => setIsOpenLabels(!isOpenLabels)}
                    aria-label="add-label"
                    title={t('commons:media.labels.add_labels')}
                />
            </div>
        );
    };

    return (
        <>
            <Post.Hover forceDisplay={isOpenLabels || isOpenAlbums} openViewer={openViewer}>
                <div className="flex w-full flex-1 items-center justify-center">
                    <button
                        aria-label="open-viewer"
                        className="flex h-12 w-12 items-center justify-center rounded-full bg-white !ring-offset-0"
                        onClick={openViewer}
                        title={t('analytics:open_media_viewer')}>
                        <Icon icon="visible" className="text-xl text-icoBlue" />
                    </button>
                </div>
                {(showAlbums || showLabels) && (
                    <div className="w-full py-2">
                        <div className="flex w-full flex-col justify-start space-y-3 px-4">
                            {renderAlbums()}
                            {renderLabels()}
                        </div>
                    </div>
                )}
            </Post.Hover>
            {isOpenAlbums &&
                ReactDOM.createPortal(
                    <CustomPopover
                        open={isOpenAlbums}
                        popperElement={popperElement}
                        setPopperElement={setPopperElement}
                        toggle={() => setIsOpenAlbums(false)}
                        styles={styles}
                        attributes={attributes}
                        width="w-80 max-w-[calc(100vw-38px)]"
                        wrapperClassName="z-1"
                        isBorder>
                        <AlbumsPopover
                            mediaAlbums={albums}
                            mediaId={data.id}
                            handleAlbumsEvent={handleAlbumsEvent}
                            postType={postType}
                            socialProfile={socialProfile}
                        />
                    </CustomPopover>,
                    document.body
                )}
            {isOpenLabels &&
                ReactDOM.createPortal(
                    <CustomPopover
                        open={isOpenLabels}
                        popperElement={popperElement}
                        setPopperElement={setPopperElement}
                        toggle={() => setIsOpenLabels(false)}
                        styles={styles}
                        attributes={attributes}
                        width="w-80 max-w-[calc(100vw-38px)]"
                        wrapperClassName="z-1"
                        isBorder>
                        <LabelsPopover
                            labelsOnMedia={labels}
                            mediaId={data.id}
                            handleLabelsEvent={handleLabelsEvent}
                            postType={postType}
                            socialProfile={socialProfile}
                        />
                    </CustomPopover>,
                    document.body
                )}
        </>
    );
};

HoverLabelAndAlbums.propTypes = {
    openViewer: PropTypes.func.isRequired,
    showAlbums: PropTypes.bool.isRequired,
    showLabels: PropTypes.bool.isRequired,
    handleAlbumsEvent: PropTypes.func,
    handleLabelsEvent: PropTypes.func
};

export default HoverLabelAndAlbums;
